// @flow
import * as React from 'react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { Confirmation, connectAPI, withConfirmation } from 'speed-js-react';
import fiberMessages from 'esm-web-pro/messages/fiberMessages';
import { injectIntl, IntlShape } from 'react-intl';
import type { UUID } from 'esm-web-pro/types/common';
import type { Fiber } from 'esm-web-pro/types/networks';
import type { Map } from 'esm-web-pro/containers/map/types';
import DamageIcon from 'esm-web-pro/components/layout/sidePanel/icons/DamageIcon';
import DamageActionIcon from 'esm-web-pro/components/layout/sidePanel/icons/DamageActionIcon';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import ConnectIcon from 'esm-web-pro/components/layout/sidePanel/icons/ConnectIcon';
import FixActionIcon from 'esm-web-pro/components/layout/sidePanel/icons/FixActionIcon';
import type { Action } from 'esm-web-pro/components/layout/sidePanel/Actions';
import { HTTP_400_BAD_REQUEST } from 'speed-js-core';
import { POINT12_CONTENT_TOOLTIP_KEY } from '../point12/CableList/Point12CableList';

type Props = {
    confirm: (confirmation: Confirmation) => void,
    intl: IntlShape,
    damageFiber: (uuid: UUID, data: { is_damaged: boolean}) => Promise<Object>,
    fiber: Fiber,
    updateObject: (data: Fiber) => void,
    map: Map,
    ActionComponent: React.ComponentType<{actions: Action[], pad?: Object | string}>,
    actionProps?: { ...Action, tooltip: boolean },
    pad?: Object,
    main?: boolean,
    editor: Object,
    damaged?: boolean,
}

const FiberDamageAction = (props: Props) => {
    const {
        confirm, intl: { formatMessage }, damageFiber, fiber, updateObject, map: { is_draft: isDraft },
        ActionComponent, actionProps, pad, main, editor, damaged,
    } = props;

    const handleMarkAsDamaged = (e: SyntheticMouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        confirm({
            message: formatMessage(fiberMessages.markDamagedTitle),
            actions: {
                cancel: {
                    label: formatMessage(fiberMessages.cancelMarkDamaged),
                    callback: () => {
                    },
                    type: 'negative',
                },
                confirm: {
                    label: formatMessage(fiberMessages.confirmMarkDamaged),
                    callback: () => {
                        if (damageFiber) {
                            damageFiber(fiber.uuid, { is_damaged: true })
                                .then((response) => {
                                    if (response.status === HTTP_400_BAD_REQUEST) {
                                        confirm({
                                            message: response.data.non_field_errors[0],
                                            actions: {
                                                cancel: {
                                                    label: formatMessage(fiberMessages.cancelMarkDamaged),
                                                    callback: () => {
                                                    },
                                                    type: 'negative',
                                                },
                                            },
                                        });
                                    } else {
                                        updateObject(response);
                                    }
                                });
                        }
                    },
                    type: 'positive',
                },
            },
        });
    };

    const handleFix = (e: SyntheticMouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        confirm({
            message: formatMessage(fiberMessages.fixTitle),
            actions: {
                cancel: {
                    label: formatMessage(fiberMessages.cancelMarkDamaged),
                    callback: () => {
                    },
                    type: 'negative',
                },
                confirm: {
                    label: formatMessage(fiberMessages.confirmMarkDamaged),
                    callback: () => {
                        if (damageFiber) {
                            damageFiber(fiber.uuid, { is_damaged: false })
                                .then((response) => {
                                    if (response.status === HTTP_400_BAD_REQUEST) {
                                        confirm({
                                            message: response.data.non_field_errors[0],
                                            actions: {
                                                cancel: {
                                                    label: formatMessage(fiberMessages.cancelMarkDamaged),
                                                    callback: () => {
                                                    },
                                                    type: 'negative',
                                                },
                                            },
                                        });
                                    } else {
                                        updateObject(response);
                                    }
                                });
                        }
                    },
                    type: 'positive',
                },
            },
        });
    };

    const { is_damaged: isDamaged } = fiber;

    const actions = [];
    const { tooltip, ...rest } = actionProps;

    const damageActionProps = {
        key: `${fiber.id}-damage`,
        icon: main ? <DamageActionIcon /> : <DamageIcon />,
        callback: handleMarkAsDamaged,
        ...rest,
        color: main ? 'transparent' : null,
        disabled: main ? editor.state.loading : false,
    };

    const fixActionProps = {
        key: `${fiber.id}-fix`,
        icon: main ? <FixActionIcon /> : <ConnectIcon />,
        callback: handleFix,
        ...rest,
        disabled: main ? editor.state.loading : false,
        color: main ? 'transparent' : null,
    };

    let damageAction;
    let fixAction;

    if (tooltip) {
        damageAction = Object.assign({},
            damageActionProps,
            { tooltip: formatMessage(actionMessages.damage), tooltipKey: POINT12_CONTENT_TOOLTIP_KEY });
        fixAction = Object.assign({},
            fixActionProps,
            { tooltip: formatMessage(actionMessages.fix), tooltipKey: POINT12_CONTENT_TOOLTIP_KEY });
    } else {
        damageAction = Object.assign({},
            damageActionProps,
            { tooltip: formatMessage(actionMessages.damage) },
            { marginVertical: '15px' });
        fixAction = Object.assign({},
            fixActionProps,
            { tooltip: formatMessage(actionMessages.fix) },
            { marginVertical: '15px' });
    }

    if (isDraft && !fiber.splice_identity) {
        if (!isDamaged && !damaged) {
            actions.push(damageAction);
        } else {
            actions.push(fixAction);
        }
    }

    return <ActionComponent actions={actions} pad={pad} main />;
};

const apiCallback = (apiClient: ApiClient) => ({
    damageFiber: {
        send: (fiberUuid, data) => apiClient.resources.fiber.damage(fiberUuid, data),
    },
});

FiberDamageAction.defaultProps = {
    actionProps: {},
    pad: { right: '5px' },
    main: false,
    damaged: false,
};

export default connectAPI(apiCallback)(withConfirmation(injectIntl(FiberDamageAction)));
